// Utility to normalize experiment documents for display in list and details pages.
// Supports legacy experiments and new custom_experiment schema produced by the Builder.
// Exported functions:
//  - normalizeExperiment(doc): returns { title, date, expt_id, agents, task, taskType, blocks, raw }
//  - deriveBlockSummary(block): helper to compute trial counts cross schema.

function safeArray(v){ return Array.isArray(v) ? v : []; }

function normalizeBlocks(taskType, rawBlocks){
  const blocks = safeArray(rawBlocks).map((b, idx) => {
    const base = { index: idx, type: b.type || 'unknown', raw: b, title: b.title || '' };
    if (taskType === 'custom_experiment') {
      if (b.type === 'stimuli') {
        base.trialCount = safeArray(b.stimuli).length;
        base.stimuliCount = base.trialCount;
        base.questionCount = safeArray(b.questions).length;
      } else if (b.type === 'questionnaire') {
        base.trialCount = safeArray(b.questions).length;
        base.questionTypes = safeArray(b.questions).map(q=>q.type).filter(Boolean);
      } else if (b.type === 'scale') {
        base.trialCount = safeArray(b.trials).length || (b.items ? b.items.length : 0);
      } else if (b.type === 'conversation') {
        base.trialCount = safeArray(b.trials).length;
        base.agentIds = [];
        safeArray(b.trials).forEach(t=> safeArray(t.agents).forEach(a=> base.agentIds.push(a)));
      } else if (b.type === 'primed_conversation') {
        base.conversationMode = b.conversationMode || 'single_for_block';
        if (b.conversationMode === 'single_for_block') {
          base.trialCount = safeArray(b.conversation?.trials).length;
          base.agentIds = [];
          safeArray(b.conversation?.trials).forEach(t=> safeArray(t.agents).forEach(a=> base.agentIds.push(a)));
          base.stimuliCount = safeArray(b.stimuli).length; // still show stimuli associated
        } else { // per_stimulus
          base.trialCount = safeArray(b.stimuli).length;
          base.stimuliCount = base.trialCount;
          base.agentIds = [];
          safeArray(b.stimuli).forEach(s=> safeArray(s.conversation?.agents).forEach(a=> base.agentIds.push(a)));
        }
        base.agentIds = Array.from(new Set(base.agentIds));
      } else {
        base.trialCount = b.num_trials || 0;
      }
      // Common derived for custom schema
      if (!base.agentIds) {
        base.agentIds = [];
      }
    } else {
      if (b.type === 'stimuli') base.trialCount = b.stimuliSet?.files?.length || 0; else base.trialCount = b.num_trials || 0;
      if (b.type === 'stimuli') {
        base.stimuliSetNames = b.stimuliSet?.name ? [b.stimuliSet.name] : [];
      }
    }
    // Stimuli set names for custom primed & stimuli blocks
    if (taskType === 'custom_experiment' && ['primed_conversation','stimuli'].includes(b.type)) {
      base.stimuliSetNames = [];
      if (Array.isArray(b.stimuli)) {
        // Try to map any stimuli that carry an origin set reference (if later added)
        // Placeholder: if stimuli objects have a setName use it.
        b.stimuli.forEach(s => { if (s.setName) base.stimuliSetNames.push(s.setName); });
      }
      base.stimuliSetNames = Array.from(new Set(base.stimuliSetNames));
    }
    return base;
  });
  return blocks;
}

export function normalizeExperiment(doc){
  if (!doc || typeof doc !== 'object') return null;
  const taskType = doc.task?.type || 'unknown';
  let title = doc.title || doc.task?.parameters?.title || doc.task?.title || 'Untitled Experiment';
  // In new schema top-level 'title' may be unset; store it if needed for inline editing compatibility.
  const blocks = normalizeBlocks(taskType, doc.task?.blocks);
  return {
    title,
    date: doc.date,
    expt_id: doc.expt_id,
    agents: Array.isArray(doc.agents) ? doc.agents : [],
    task: doc.task || {},
    taskType,
    blocks,
    raw: doc
  };
}

export function deriveTotals(norm){
  if (!norm) return { totalTrials:0, numBlocks:0 };
  const totalTrials = norm.blocks.reduce((acc,b)=> acc + (b.trialCount||0),0);
  return { totalTrials, numBlocks: norm.blocks.length };
}
